import React from 'react';
import { markdownToEscPos } from '../../utils/markdownEscpos';
import MarkdownEditor from '../../components/MarkdownEditor/MarkdownEditor';
import { usePrinter } from '../../contexts/PrinterContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import './MarkdownPage.css';

const MarkdownPage: React.FC = () => {
  // Use the global printer context
  const { 
    printer, 
    status, 
    connectPrinter,
    debugMode,
    debugInfo
  } = usePrinter();

  const handlePrintDocument = async (markdownContent: string) => {
    try {
      // Connect to printer if not already connected
      if (!printer) {
        await connectPrinter();
        if (!printer) {
          return; // Connection failed, the context will update the status
        }
      }
      
      // Convert markdown to ESC/POS commands
      const printerCommands = markdownToEscPos(markdownContent);
      
      // Use the printer context to send the commands
      if (printer) {
        // We're using the raw sendToPrinter here because we have custom ESC/POS commands
        // that are different from the receipt format
        const { sendToPrinter } = await import('../../utils/printer');
        await sendToPrinter(printer, printerCommands);
      } else {
        // If thermal printer is not available, use browser print
        window.print();
      }
    } catch (error) {
      console.error('Error printing document:', error);
    }
  };

  // No need for handleDisconnect or toggleDebugMode as they're handled by the PrinterContext

  return (
    <div className="markdown-page">
      <div className="page-header">
        <h1>Freehand Document Writer</h1>
        <div className="printer-status-container">
          <div className="printer-status">
            <span className={`status-indicator ${printer ? 'connected' : 'disconnected'}`}>
              {printer ? 'Printer Connected' : 'Printer Disconnected'}
            </span>
            <a href="/settings" className="settings-link" title="Printer Settings">
              <FontAwesomeIcon icon={faCog} />
            </a>
          </div>
        </div>
      </div>
      
      {status && (
        <div className="status-message">
          {status}
        </div>
      )}
      
      {debugMode && debugInfo && (
        <div className="debug-info">
          <h3>Debug Information</h3>
          <pre>{debugInfo}</pre>
        </div>
      )}
      
      <MarkdownEditor onPrintDocument={handlePrintDocument} />
    </div>
  );
};

export default MarkdownPage;
